// Parent feis registration, /register-dancers?id=<event> (MC2 card 9a9e350b,
// Devin 2026-10-16/17; mocks in ~/.mission-control/launches/2eb2ed6d-…/
// registration-mockup). One screen to pick dances, one to review and pay:
//   - one tab per child (none for a single child) plus "Add dancer";
//   - a level carousel per child, opened on their last feis's highest level;
//     each level shows its dances, second chances under their dance, and the
//     specials for that level; Teams and Just for fun at the end;
//   - the server quote is the price; picks are kept as a draft;
//   - Review & pay lists each child with Edit, the fee lines (one Platform
//     Fee line) and the payment form.
// The layout rules live in registrationModel.js (shared blueprint for the app).
//
// The same screen changes an entered child's dances ("Change dances",
// /feis-edit-dancers/<event>?regId=<registration>): only that child, their
// current dances ticked, priced by the server's change quote (the difference
// at today's prices plus the fee; removals aren't refunded), and Review
// lists what is added and removed.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import ParentHeader from "../ParentHeader/ParentHeader";
import Loader from "../../Components/Loader/Loader";
import DancerExistsPanel from "../../Components/Modals/DancerExistsPanel";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import { formatDate } from "../../helper/formatDate";
import { directLinkKeyFor, rememberDirectLinkKey } from "../../helper/directLink";
import favIcon from "../../Assets/favicon.ico";
import { useServerQuote, quoteFromPriceChange } from "../Register-Events/useServerQuote";
import {
  buildPages,
  startPageKey,
  togglePick,
  dancePickedElsewhere,
  compIndex,
  summaryLines,
  summarySections,
  saveDraft,
  loadDraft,
  removeFromDraft,
  ageCodesFor,
  highestLevel,
  money,
  withKeptEntries,
} from "./registrationModel";
import { AddDancerSheet, ChildTabs, DancerSummary, FeeLines, Icon, LevelBar, PageList, SummarySections } from "./parts";
import { PayPanel } from "./PayPanel";
import { lateEntriesEnd } from "../../helper/lateEntries";
import "../../Teacher/Oireachtas/oireachtas.css";
import "./register.css";

const API = process.env.REACT_APP_BASE_URL;
const MAX_DANCERS = 5; // the account's limit elsewhere (Parent/AddDancers.jsx)
// The success page's summary, per payment ("free" when nothing was owed), so
// a later payment at the same event never changes what an earlier one shows.
export const successStoreKey = (eventId, paymentId) => `feisRegSuccess:${eventId}:${paymentId || "free"}`;

const dayText = (d, f = "MMM D") => (d ? moment(formatDate(d)).format(f) : "");
const dayStart = (d) => moment(formatDate(d)).startOf("day");
// the last day any entry was taken: the late entries end day when it is after the close
const lastEntryDay = (event) => {
  const lateEnd = lateEntriesEnd(event);
  return lateEnd && event.closing_date && dayStart(lateEnd).isAfter(dayStart(event.closing_date)) ? lateEnd : event.closing_date;
};

/**
 * "open", "late" (closed, but still taking late entries until the late
 * entries end day, or the organizer's Direct Link lets them in), "not_open"
 * or "closed".
 */
const windowState = (event, hasKey) => {
  if (!event) return "open";
  const today = moment().startOf("day");
  if (event.opening_date && today.isBefore(dayStart(event.opening_date))) return hasKey ? "open" : "not_open";
  if (event.closing_date && today.isAfter(dayStart(event.closing_date))) {
    const lateEnd = lateEntriesEnd(event);
    return hasKey || (lateEnd && !today.isAfter(dayStart(lateEnd))) ? "late" : "closed";
  }
  return "open";
};

// One screen per event: switching events (same route, new ?id=) remounts it,
// so nothing from one event (picks, draft, payment) leaks into another.
const RegisterPage = () => {
  const { search } = useLocation();
  const eventId = new URLSearchParams(search).get("id") || "";
  return <EventRegistration key={eventId} eventId={eventId} />;
};

/** /feis-edit-dancers/:id?regId= : change an entered child's dances. */
export const ChangeDancesPage = () => {
  const { id } = useParams();
  const { search } = useLocation();
  const regId = new URLSearchParams(search).get("regId") || "";
  return <EventRegistration key={`${id}:${regId}`} eventId={id || ""} regId={regId} />;
};

const EventRegistration = ({ eventId, regId }) => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const editing = Boolean(regId);
  const step = params.get("step") === "review" ? "review" : "pick";
  useEffect(() => rememberDirectLinkKey(location.search), [location.search]);
  const linkKey = directLinkKeyFor(eventId);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [event, setEvent] = useState(null);
  const [dancers, setDancers] = useState([]);
  const [syllabus, setSyllabus] = useState({});
  const [groups, setGroups] = useState({});
  const [picks, setPicks] = useState({});
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [chosenId, setActiveId] = useState(null);
  const [activePage, setActivePage] = useState({});
  const [entries, setEntries] = useState({}); // registrationId -> competition ids
  const [sheet, setSheet] = useState(null); // null | {exists?}
  const [schools, setSchools] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  // Changing an entry: the registration's child and dances as they are now.
  const [original, setOriginal] = useState(null); // {dancerId, ids}

  // Changing an entry shows only that child, as one still being entered.
  const loadDancers = useCallback(async (onlyId) => {
    const res = await serviceConsumer("GET", `${API}/account/dancers`, { feisId: eventId });
    let list = (res?.dancers || []).slice().sort((a, b) => birthOrder(a) - birthOrder(b));
    if (onlyId) list = list.filter((d) => String(d._id) === onlyId).map((d) => ({ ...d, isRegistered: false }));
    setDancers(list);
    return list;
  }, [eventId]);

  useEffect(() => {
    document.title = "Feis.link - Register";
    if (!eventId) {
      setLoadError("This registration link is missing its event.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const reg = editing ? (await serviceConsumer("GET", `${API}/registration/regdetails/${regId}`))?.result?.[0] : null;
        if (editing && (!reg || String(reg.event_id || eventId) !== String(eventId))) {
          throw { messages: "We couldn't find this entry. It may have been changed or removed." };
        }
        const [ev, , syl] = await Promise.all([
          serviceConsumer("GET", `${API}/feis/${eventId}`),
          loadDancers(reg ? String(reg.dancer_id) : null),
          serviceConsumer("GET", `${API}/feis/levelwise/competitions/${eventId}`),
        ]);
        if (reg) setOriginal({ dancerId: String(reg.dancer_id), ids: (reg.competitions || []).map(String) });
        setEvent(ev?.Feis || null);
        setSyllabus(syl?.result || {});
        setGroups(syl?.groups || {});
      } catch (e) {
        setLoadError(e?.messages || "We couldn't load this feis. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, regId, loadDancers]);

  const index = useMemo(() => compIndex(syllabus), [syllabus]);
  // Changing an entry starts on (and keeps the levels of) the entry itself.
  const editLevel = useMemo(() => (original ? highestLevel(original.ids, index, groups) : null), [original, index, groups]);
  const layouts = useMemo(() => {
    const out = {};
    dancers.forEach((d) => {
      const dancer = editLevel ? { ...d, lastLevel: editLevel } : d;
      out[String(d._id)] = buildPages({ syllabus, groups, dancer, feisDate: event?.feis_date });
    });
    return out;
  }, [dancers, syllabus, groups, event, editLevel]);
  const eligible = useMemo(
    () => Object.fromEntries(Object.entries(layouts).map(([id, l]) => [id, l.pages.length > 0])),
    [layouts]
  );

  // The draft comes back once everything is loaded; then every change is saved.
  // (Changing an entry starts from the entry and keeps no draft.)
  useEffect(() => {
    if (loading || draftLoaded || !event) return;
    if (editing) {
      // every dance on the entry, including any no longer offered: those
      // can't be shown or unticked here, so they stay as they are
      if (original) setPicks({ [original.dancerId]: [...original.ids] });
    } else {
      setPicks(loadDraft(eventId, { dancers, index }));
    }
    setDraftLoaded(true);
  }, [loading, draftLoaded, event, eventId, dancers, index, editing, original]);
  useEffect(() => {
    if (draftLoaded && !editing) saveDraft(eventId, picks);
  }, [picks, draftLoaded, eventId, editing]);

  // The tapped child, else the first child who can still be entered.
  const activeId =
    chosenId && dancers.some((d) => String(d._id) === chosenId)
      ? chosenId
      : (() => {
        const first = dancers.find((d) => !d.isRegistered && eligible[String(d._id)]) || dancers[0];
        return first ? String(first._id) : null;
      })();

  const active = dancers.find((d) => String(d._id) === activeId) || null;
  const layout = active ? layouts[activeId] : null;
  // Changing an entry: its dances no page offers this dancer stay visible
  // (ticked, untick to remove) on their own level's page.
  const { pages, kept } = useMemo(
    () =>
      editing && original && layout && original.dancerId === activeId
        ? withKeptEntries(layout.pages, original.ids, index)
        : { pages: layout?.pages || [], kept: new Set() },
    [editing, original, layout, activeId, index]
  );
  // something to show for the active child: offered dances, or (changing an
  // entry) dances already on it that are no longer offered
  const canPick = pages.length > 0;
  const pageKey =
    activePage[activeId] && pages.some((p) => p.key === activePage[activeId])
      ? activePage[activeId]
      : startPageKey(pages, editLevel || active?.lastLevel);
  const page = pages.find((p) => p.key === pageKey) || null;
  const picked = picks[activeId] || [];

  // A dance is entered at one level: its row on the other levels is greyed
  // ("Picked at Novice"), and ticking it there moves it.
  const elsewhere = dancePickedElsewhere(pages, picked);
  const toggle = (compId) =>
    setPicks((prev) => ({ ...prev, [activeId]: togglePick(pages, prev[activeId] || [], compId) }));
  const goPage = (key) => setActivePage((prev) => ({ ...prev, [activeId]: key }));

  // Swipe the list left/right to move between levels (phones).
  const touch = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    touch.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    const s = touch.current;
    touch.current = null;
    if (!t || !s || !page) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const i = pages.findIndex((p) => p.key === pageKey);
    const next = pages[i + (dx < 0 ? 1 : -1)];
    if (next) goPage(next.key);
  };

  // An entered child's current dances (for the "is entered" view).
  useEffect(() => {
    const regId = active?.isRegistered && active.registrationId ? String(active.registrationId) : null;
    if (!regId || entries[regId]) return;
    serviceConsumer("GET", `${API}/registration/regdetails/${regId}`)
      .then((res) => setEntries((prev) => ({ ...prev, [regId]: res?.result?.[0]?.competitions || [] })))
      .catch(() => setEntries((prev) => ({ ...prev, [regId]: [] })));
  }, [active, entries]);

  // ---- price -------------------------------------------------------------
  const families = editing
    ? original
      ? [{ dancer_id: original.dancerId, competitions: picks[original.dancerId] || [] }]
      : []
    : dancers
      .filter((d) => !d.isRegistered && (picks[String(d._id)] || []).length)
      .map((d) => ({ dancer_id: String(d._id), competitions: picks[String(d._id)] }));
  const pickCount = families.reduce((s, f) => s + f.competitions.length, 0);
  // Changing an entry: what is added and removed against the entry as it is.
  const nowIds = editing ? families[0]?.competitions || [] : [];
  const added = editing && original ? nowIds.filter((id) => !original.ids.includes(id)) : [];
  const removed = editing && original ? original.ids.filter((id) => !nowIds.includes(id)) : [];
  const changed = added.length + removed.length > 0;
  const canReview = editing ? changed : pickCount > 0;
  const { quote, failed: quoteFailed, error: quoteError, retry: retryQuote, beginSubmission, acceptCorrection } = useServerQuote({
    enabled: !!event && (editing ? changed : families.length > 0),
    path: editing ? "/stripe/edit/quote" : "/stripe/quote",
    body: editing
      ? { registration_id: regId, dancer_id: original?.dancerId, competitions: nowIds, directLinkKey: linkKey }
      : { event_id: eventId, org_id: event?.organizer_id, data: families, directLinkKey: linkKey },
  });
  const itemsCents = families.reduce(
    (s, f) => s + f.competitions.reduce((t, id) => t + Math.round(Number(index.get(String(id))?.price || 0) * 100), 0),
    0
  );
  const barTotal = quote
    ? money(quote.totalCents)
    : editing
      ? changed && !quoteFailed ? "…" : changed ? "—" : money(0)
      : pickCount
        ? quoteFailed ? "—" : `${money(itemsCents)} + fees`
        : money(0);
  const whoLine = editing
    ? [added.length && `Adding ${added.length}`, removed.length && `Removing ${removed.length}`].filter(Boolean).join(" · ")
    : dancers
      .filter((d) => !d.isRegistered && (picks[String(d._id)] || []).length)
      .map((d) => `${d.fname} ${(picks[String(d._id)] || []).length}`)
      .join(" · ");

  // ---- review & pay ------------------------------------------------------
  const [checkout, setCheckout] = useState(null); // {key, clientSecret, intentId}
  // A checkout error belongs to the selection it was for: going back to a
  // selection whose payment form is ready shows the form, not an old error.
  const [checkoutFailure, setCheckoutFailure] = useState(null); // {key, message}
  const [priceNote, setPriceNote] = useState("");
  const [checkoutTry, setCheckoutTry] = useState(0);
  // Nothing to pay (a change that only removes dances, free entries): saved
  // only when the parent taps the button, never just by opening Review.
  // (the confirmation is for that exact selection and total)
  const [confirmedKey, setConfirmedKey] = useState("");
  // Each checkout request belongs to one generation; leaving review, a new
  // selection, a retry or leaving the screen starts a new generation, so an
  // answer still on its way from an older one is ignored (no payment form,
  // no navigation, no error for a selection the parent no longer has).
  const generation = useRef(0);
  useEffect(() => () => {
    generation.current += 1;
  }, []);
  const checkoutKey = quote ? JSON.stringify([families, quote.totalCents]) : "";

  useEffect(() => {
    generation.current += 1;
    if (step !== "review" || !event || !quote || !checkoutKey) return;
    if (checkout?.key === checkoutKey) return;
    if (quote.totalCents === 0 && confirmedKey !== checkoutKey) return;
    const mine = generation.current;
    const current = () => generation.current === mine;
    const submission = beginSubmission();
    setCheckoutFailure(null);
    const request = editing
      ? serviceConsumer("POST", `${API}/stripe/edit/checkout`, {
        registration_id: regId,
        dancer_id: original.dancerId,
        competitions: nowIds,
        expectedTotalCents: quote.totalCents,
        directLinkKey: linkKey,
      })
      : serviceConsumer("POST", `${API}/stripe/checkout`, {
        event_id: eventId,
        org_id: event.organizer_id,
        data: families,
        expectedTotalCents: quote.totalCents,
        directLinkKey: linkKey,
      });
    request
      .then((res) => {
        if (!current()) return;
        const pi = res?.checkout;
        if (pi?.client_secret) {
          setCheckout({ key: checkoutKey, clientSecret: pi.client_secret, intentId: pi.id });
        } else if (res?.success === true || res?.success === "Payment_Pending") {
          // nothing to pay: the server saved it already. The summary is
          // kept under its own token, so another free save at this event
          // never shows on this one's page.
          const token = `free-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
          rememberSuccess(token);
          if (!editing) removeFromDraft(eventId, Object.fromEntries(families.map((f) => [f.dancer_id, f.competitions])));
          navigate(`${successPath}&free=1&save=${token}`, { replace: true });
        }
      })
      .catch((e) => {
        if (!current()) return;
        if (acceptCorrection(quoteFromPriceChange(e), submission)) {
          setPriceNote("The prices changed since you started. This is the new total.");
        } else {
          setCheckoutFailure({ key: checkoutKey, message: e?.messages || "We couldn't start the payment. Please try again." });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, checkoutKey, checkoutTry, confirmedKey]);
  const retryCheckout = () => setCheckoutTry((n) => n + 1);
  const checkoutError =
    checkoutFailure && checkoutFailure.key === checkoutKey && checkout?.key !== checkoutKey ? checkoutFailure.message : "";
  // The payment whose form is on screen now (Review, this selection, this
  // page). A confirmation can finish after the parent moved on: its answer
  // counts only while its form is still the one shown.
  const shownPayForm = useRef(null);
  shownPayForm.current =
    step === "review" && !checkoutError && checkout?.key === checkoutKey ? checkout.intentId : null;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const isShownPayForm = (intentId) => mounted.current && shownPayForm.current === intentId;

  // Review is this page with ?step=review. Back to picking goes back in
  // history only when we know the previous entry is this event's picking
  // step (we pushed review from it); otherwise it swaps the URL.
  const goReview = () =>
    navigate(`${location.pathname}?${withParam(location.search, "step", "review")}`, { state: { fromPick: true } });
  const backToPick = (dancerId) => {
    if (dancerId) setActiveId(String(dancerId));
    if (step === "review" && location.state?.fromPick) navigate(-1);
    else navigate(`${location.pathname}?${withParam(location.search, "step", null)}`, { replace: true });
  };
  useEffect(() => {
    // a reload of the review step with nothing to review goes back to picking
    if (step === "review" && draftLoaded && !canReview) backToPick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, draftLoaded, canReview]);

  // What the success page shows (Stripe returns there after paying).
  const rememberSuccess = (paymentId) => {
    try {
      sessionStorage.setItem(
        successStoreKey(eventId, paymentId),
        JSON.stringify({
          name: event?.name,
          date: event?.feis_date,
          // exactly what this payment covers: cleared from the draft once paid
          paid: Object.fromEntries(families.map((f) => [f.dancer_id, f.competitions])),
          dancers: dancers
            .filter((d) => (!d.isRegistered && (picks[String(d._id)] || []).length) || (editing && String(d._id) === original?.dancerId))
            .map((d) => ({
              id: String(d._id),
              fname: d.fname,
              lname: d.lname,
              lines: summaryLines(picks[String(d._id)], index, groups).map((l) => l.label),
              // the dances as the review lists them: by level, one per line, a 2nd chance under its dance
              sections: summarySections(picks[String(d._id)], index, groups).map((sec) => ({
                key: sec.key,
                title: sec.title,
                rows: sec.rows.map(({ id, name, sub, secondOnly }) => ({ id, name, sub, secondOnly })),
              })),
            })),
        })
      );
    } catch (_) { }
  };
  const successPath = editing
    ? `/parent/feis/edit-register/payment/success?id=${eventId}&reg=${regId}`
    : `/parent/feis/register/payment/success?id=${eventId}`;
  const successUrl = `${process.env.REACT_APP_BASE}${successPath}`;

  // ---- add a dancer ------------------------------------------------------
  const openSheet = async () => {
    setSaveError("");
    setSheet({});
    if (!schools) {
      try {
        const res = await serviceConsumer("GET", `${API}/account/getSchools`);
        setSchools(res?.result || []);
      } catch (_) {
        setSchools([]);
      }
    }
  };
  const saveDancer = async (body) => {
    setSaving(true);
    setSaveError("");
    try {
      const res = await serviceConsumer("POST", `${API}/account/dancers`, body);
      await loadDancers();
      if (res?.dancer?._id) setActiveId(String(res.dancer._id));
      setSheet(null);
    } catch (e) {
      if (e?.error?.response?.status === 409) {
        setSheet({ exists: { name: `${body.first_name} ${body.last_name}`, birthYear: body.date_birth } });
      } else {
        setSaveError(e?.messages || "We couldn't save the dancer. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- render ------------------------------------------------------------
  // (Changing an existing entry isn't limited to the entry window, as before.)
  const state = editing ? "open" : windowState(event, !!linkKey);
  const closeDay = dayText(event?.closing_date);
  const lateEnd = lateEntriesEnd(event);
  const lateUntil =
    state === "late" && lateEnd && !moment().startOf("day").isAfter(dayStart(lateEnd)) ? dayText(lateEnd, "ddd MMM D") : "";
  const lateCents = (quote?.lines || []).find((l) => l.type === "late_fee")?.amountCents || 0;

  const header = (
    <div className="rg-head">
      <button type="button" className="rg-back" aria-label="Back" onClick={() => (step === "review" ? backToPick() : navigate(-1))}>
        <Icon name="arrow_back" />
      </button>
      <img className="rg-logo" src={event?.logo || favIcon} alt="" />
      <div className="rg-head-t">
        <p className="rg-en">
          {editing
            ? step === "review" ? "Review changes" : `Change ${dancers[0]?.fname || ""}'s dances`
            : step === "review" ? "Review & pay" : event?.name || "Register"}
        </p>
        <p className="rg-ed">
          {step === "review" || editing ? (
            `${event?.name || ""} · ${dayText(event?.feis_date, "ddd MMM D")}`
          ) : (
            <>
              {[dayText(event?.feis_date, "ddd MMM D"), event?.venue_city].filter(Boolean).join(" · ")}
              {state === "late" ? <span className="late"> · Late entries</span> : closeDay ? ` · Entries close ${closeDay}` : ""}
            </>
          )}
        </p>
      </div>
    </div>
  );

  // Until the draft (or the entry being changed) is in, picks would flash empty.
  if (loading || (!loadError && event && !draftLoaded)) {
    return (
      <div className="oir rg"><ParentHeader /><Loader /></div>
    );
  }
  if (loadError || !event) {
    return (
      <div className="oir rg">
        <ParentHeader />
        <div className="rg-wrap"><div className="rg-empty">{loadError || "This feis isn't available."}</div></div>
      </div>
    );
  }
  if (state === "closed" || state === "not_open") {
    return (
      <div className="oir rg">
        <ParentHeader />
        <div className="rg-wrap">
          {header}
          <div className="rg-main">
            <div className="rg-note late">
              <Icon name="event_busy" />
              <div>
                {state === "closed"
                  ? `Registration for ${event.name} closed on ${dayText(lastEntryDay(event), "MMMM D")}. Please contact the organizer.`
                  : `Registration for ${event.name} opens on ${dayText(event.opening_date, "MMMM D")}.`}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const summary = (withEdit) => {
    if (editing) {
      const d = dancers[0];
      if (!d) return null;
      const block = (title, ids, note) =>
        ids.length > 0 && (
          <div className="rg-sum-d">
            <div className="rg-sum-h"><span className="nm">{title}<small>{note}</small></span></div>
            <SummarySections sections={summarySections(ids, index, groups)} />
          </div>
        );
      return (
        <>
          <DancerSummary dancer={d} count={nowIds.length} sections={summarySections(nowIds, index, groups)} onEdit={withEdit ? () => backToPick() : undefined} />
          {block("Adding", added, "")}
          {block("Removing", removed, "Removed dances aren't refunded")}
          {!changed && <div className="rg-sum-l" style={{ padding: "4px 16px 12px" }}><span>No changes yet</span><span /></div>}
        </>
      );
    }
    const withPicks = dancers.filter((d) => !d.isRegistered && (picks[String(d._id)] || []).length);
    return (
      <>
        {withPicks.map((d) => (
          <DancerSummary
            key={d._id}
            dancer={d}
            count={picks[String(d._id)].length}
            sections={summarySections(picks[String(d._id)], index, groups)}
            onEdit={withEdit ? () => backToPick(d._id) : undefined}
          />
        ))}
        {withPicks.length === 0 && <div className="rg-sum-l" style={{ padding: "4px 16px 12px" }}><span>Nothing picked yet</span><span /></div>}
      </>
    );
  };

  if (step === "review") {
    return (
      <div className="oir rg">
        <ParentHeader />
        <div className="rg-wrap">
          {header}
          <div className="rg-cols" style={{ display: "block" }}>
            <div className="rg-main rg-review">
              <div>
                {priceNote && <div className="rg-note amber"><Icon name="info" /><div>{priceNote}</div></div>}
                <div className="rg-sum-cards">{summary(true)}</div>
                <div className="rg-card"><FeeLines quote={quote} loading failed={quoteFailed} error={quoteError} onRetry={retryQuote} /></div>
              </div>
              <div>
                {checkoutError && (
                  <div className="rg-note error">
                    <Icon name="error" />
                    <div>
                      {checkoutError}
                      <div>
                        <button type="button" className="rg-link" onClick={retryCheckout}><Icon name="refresh" />Try again</button>
                        <button type="button" className="rg-link" onClick={() => backToPick()}>Back to entries</button>
                      </div>
                    </div>
                  </div>
                )}
                {!checkoutError && quote && quote.totalCents === 0 && checkout?.key !== checkoutKey ? (
                  <div className="rg-pay">
                    <h4>{editing ? "Nothing more to pay" : "Nothing to pay"}</h4>
                    <button type="button" className="rg-cta" disabled={confirmedKey === checkoutKey} onClick={() => setConfirmedKey(checkoutKey)}>
                      <Icon name="check" />
                      {confirmedKey === checkoutKey ? "Saving…" : editing ? "Save changes" : "Confirm entry"}
                    </button>
                  </div>
                ) : !checkoutError && checkout?.key === checkoutKey ? (
                  <PayPanel
                    clientSecret={checkout.clientSecret}
                    intentId={checkout.intentId}
                    label={`Pay ${String(quote?.currency || "").toUpperCase()} $${(Number(quote?.totalCents || 0) / 100).toFixed(2)}`.replace("Pay  $", "Pay $")}
                    returnUrl={successUrl}
                    onBeforePay={() => rememberSuccess(checkout.intentId)}
                    onSimulated={(id) => navigate(`${successPath}&payment_intent=${id}&redirect_status=succeeded`)}
                    onReplaced={() => {
                      // opened again elsewhere (another tab or device): get a
                      // fresh payment for what's on this screen and say why
                      if (!isShownPayForm(checkout.intentId)) return;
                      setPriceNote("Review & pay was opened again in another tab or on another device, so this payment was replaced. Nothing was charged. Check the total, then pay.");
                      setCheckout(null);
                      retryCheckout();
                    }}
                    onPaid={() => {
                      // paid in another tab or on another device: it covered
                      // this selection, so show its confirmation
                      if (!isShownPayForm(checkout.intentId)) return;
                      navigate(`${successPath}&payment_intent=${checkout.intentId}&redirect_status=succeeded`);
                    }}
                  />
                ) : (
                  !checkoutError && !quoteFailed && <div className="rg-pay"><div className="rg-empty" style={{ padding: 18 }}>Getting the payment ready…</div></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ages = active ? ageCodesFor(active, event.feis_date) : [];
  const enteredIds = active?.isRegistered ? entries[String(active.registrationId)] : null;
  // after an entered child, the next one who can still be entered
  const nextToEnter = active?.isRegistered
    ? [...dancers.slice(dancers.indexOf(active) + 1), ...dancers.slice(0, dancers.indexOf(active))].find(
      (d) => !d.isRegistered && eligible[String(d._id)]
    )
    : null;
  const showTabs = dancers.length > 1 && !editing;
  const sibling = dancers[0];

  return (
    <div className="oir rg">
      <ParentHeader />
      <div className="rg-wrap">
        {header}
        {showTabs && (
          <ChildTabs
            dancers={dancers}
            activeId={activeId}
            picks={picks}
            eligible={eligible}
            onPick={setActiveId}
            onAdd={openSheet}
            canAdd={dancers.length < MAX_DANCERS}
          />
        )}
        <div className="rg-cols">
          <div className="rg-main" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {state === "late" && (
              <div className="rg-note late">
                <Icon name="schedule" />
                <div>
                  Regular entries closed {dayText(event.closing_date, "MMM D")}.
                  {lateUntil ? <> Late entries until <b>{lateUntil}</b>.</> : null}
                  {lateCents > 0 ? <> Late entries add <b>{money(lateCents)}</b>.</> : " A late fee may apply."}
                </div>
              </div>
            )}
            {!active && (
              <div className="rg-empty">
                Add your dancer to register for {event.name}.
                <div style={{ marginTop: 14 }}><button type="button" className="rg-cta" onClick={openSheet}><Icon name="add" />Add a dancer</button></div>
              </div>
            )}
            {active && !active.isRegistered && (
              <div className="rg-who">
                <div>
                  <p className="nm">{`${active.fname} ${active.lname}`}</p>
                  <p className="mt">{[ages[0] && ageLabel(ages), active.gender === "Boys/Men" ? "Boys" : "Girls"].filter(Boolean).join(" · ")}</p>
                </div>
                {!showTabs && !editing && dancers.length < MAX_DANCERS && (
                  <button type="button" className="rg-link" onClick={openSheet}><Icon name="person_add" />Add dancer</button>
                )}
              </div>
            )}

            {active?.isRegistered && (
              // An entered child: their entry as on the review (no prices,
              // it's paid), a way to change it, and the next child to enter.
              <div className="rg-sum-cards rg-entered">
                <div className="rg-sum-d">
                  <div className="rg-sum-h">
                    <Icon name="check_circle" />
                    <span className="nm">
                      {active.fname} is entered
                      <small>{enteredIds ? `${enteredIds.length === 1 ? "1 dance" : `${enteredIds.length} dances`} · ${event.name}` : event.name}</small>
                    </span>
                    <button
                      type="button"
                      className="rg-edit"
                      aria-label={`Change ${active.fname}'s dances`}
                      onClick={() => navigate(`/feis-edit-dancers/${eventId}?regId=${active.registrationId}`, { state: { event } })}
                    >
                      <Icon name="edit" />Change
                    </button>
                  </div>
                  <SummarySections sections={enteredIds ? summarySections(enteredIds, index, groups) : []} prices={false} />
                </div>
                {nextToEnter && (
                  <button type="button" className="rg-cta" onClick={() => setActiveId(String(nextToEnter._id))}>
                    Enter {nextToEnter.fname} next <Icon name="arrow_forward" />
                  </button>
                )}
                {!nextToEnter && !showTabs && !editing && dancers.length < MAX_DANCERS && (
                  // the only child is entered: entering another starts by adding them
                  <button type="button" className="rg-cta" onClick={openSheet}>
                    <Icon name="person_add" />Add a dancer
                  </button>
                )}
              </div>
            )}

            {active && !active.isRegistered && !canPick && (
              <div className="rg-note amber">
                <Icon name="info" />
                <div>There's nothing at this feis for {active.fname}'s age group{ages[0] ? ` (${ageLabel(ages)})` : ""}.</div>
              </div>
            )}

            {active && !active.isRegistered && canPick && page && (
              <>
                {showTabs ? null : null}
                <LevelBar pages={pages} activeKey={pageKey} picked={picked} onPick={goPage} />
                <div style={{ height: 12 }} />
                {!editing && layout.firstFeis && page.kind === "level" && !ages.includes("19 AND OVER") && picked.length === 0 && (
                  <div className="rg-note green">
                    <Icon name="info" />
                    <div>First feis? Most dancers start in <b>Beginner</b>. Ask {active.fname}'s teacher if you're not sure.</div>
                  </div>
                )}
                <PageList
                  page={page}
                  picked={picked}
                  elsewhere={elsewhere}
                  kept={kept}
                  keptNote={`Not offered for ${active.fname}'s age. Untick to remove it.`}
                  onToggle={toggle}
                  teamNote={
                    page.kind === "teams" ? (
                      <div className="rg-note green">
                        <Icon name="groups" />
                        <div>Your teacher puts the teams together. Only enter the teams your teacher asked {active.fname} to dance in.</div>
                      </div>
                    ) : null
                  }
                />
              </>
            )}
          </div>

          <aside className="rg-side" aria-label="Your entries">
            <div className="rg-panel">
              <div className="rg-panel-h">{editing ? "Changes" : "Your entries"}</div>
              {summary(false)}
              <FeeLines quote={quote} loading={editing ? changed : pickCount > 0} failed={quoteFailed} error={quoteError} onRetry={retryQuote} />
              <button type="button" className="rg-cta" disabled={!canReview} onClick={goReview}>
                {editing ? "Review changes" : "Review & pay"} <Icon name="arrow_forward" />
              </button>
            </div>
          </aside>
        </div>
      </div>

      <div className="actionbar rg-phone-only">
        <div className="rg-bar-sum">
          <span>{whoLine || (editing ? "No changes yet" : "Nothing picked yet")}</span>
          <b>{barTotal}</b>
        </div>
        <button type="button" className="rg-cta" disabled={!canReview} onClick={goReview}>
          {editing ? "Review changes" : "Review & pay"} <Icon name="arrow_forward" />
        </button>
      </div>

      {sheet && (
        <AddDancerSheet
          schools={schools}
          sibling={sibling}
          saving={saving}
          error={saveError}
          onSave={saveDancer}
          onClose={() => setSheet(null)}
        >
          {sheet.exists ? (
            <DancerExistsPanel name={sheet.exists.name} birthYear={sheet.exists.birthYear} onBack={() => setSheet({})} />
          ) : null}
        </AddDancerSheet>
      )}
    </div>
  );
};

/** Older children first, so tabs read the same way every time. */
const birthOrder = (d) => Number(String(d?.birthday || "").slice(0, 4)) || 9999;

/** "Under 10", or "Adult" for the 19-and-over codes. */
const ageLabel = (codes) => {
  const u = (codes || []).find((c) => /^U\d+$/.test(c));
  if (u) return `Under ${u.slice(1)}`;
  return codes?.length ? "Adult" : "";
};

/** The query string with one parameter set (or removed with null). */
const withParam = (search, key, value) => {
  const p = new URLSearchParams(search);
  if (value === null) p.delete(key);
  else p.set(key, value);
  return p.toString();
};

export default RegisterPage;
